// apps/app/src/router.tsx

import { lazy, useEffect, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/layout/app-shell';
import { useAuth } from './lib/auth';
import { can, type Capability } from './lib/permissions';
import { useQuery } from '@tanstack/react-query';
import { http } from './lib/api-client';
import { keys } from './lib/query-client';
import { WaitingForOwner } from './features/billing/waiting-for-owner';

import { LoginPage } from './features/auth/login.page';
import { RegisterPage } from './features/auth/register.page';
import { ResetPage } from './features/auth/reset.page';
import { AcceptInvitePage } from './features/auth/accept-invite.page';

const loaders: (() => Promise<unknown>)[] = [];

/** `lazy`, remembering the loader so the chunk can be fetched before it is needed. */
function page<T extends ComponentType>(load: () => Promise<{ default: T }>) {
  loaders.push(load);
  return lazy(load);
}

/**
 * Fetches every route chunk once the browser is idle. The split still keeps
 * them out of the first paint; this only stops the first click on each page
 * from waiting on the network. Import promises are cached, so repeats are free.
 */
function preloadRoutes(): void {
  const run = () => loaders.forEach((load) => void load().catch(() => undefined));
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

// Route-level splitting: the dashboard is left open all day, but most of these
// screens are opened rarely and should not be in the first paint.
const DashboardPage = page(() => import('./features/dashboard/dashboard.page'));
const ScansPage = page(() => import('./features/scans/scans.page'));
const ScanDetailPage = page(() => import('./features/scans/scan-detail.page'));
const PatientsPage = page(() => import('./features/patients/patients.page'));
const PatientDetailPage = page(() => import('./features/patients/patient-detail.page'));
const TreatmentsPage = page(() => import('./features/treatments/treatments.page'));
const ProductsPage = page(() => import('./features/products/products.page'));
const LeadsPage = page(() => import('./features/leads/leads.page'));
const AnalyticsPage = page(() => import('./features/analytics/analytics.page'));
const BillingPage = page(() => import('./features/billing/billing.page'));
const StartTrialPage = page(() => import('./features/billing/start-trial.page'));
const TeamPage = page(() => import('./features/team/team.page'));
const SettingsPage = page(() => import('./features/settings/settings.page'));
const DomainsPage = page(() => import('./features/domains/domains.page'));
const OrdersPage = page(() => import('./features/orders/orders.page'));
const WidgetConfigPage = page(() => import('./features/widget/config.page'));
const WidgetInstallPage = page(() => import('./features/widget/install.page'));
const WidgetTestPage = page(() => import('./features/widget/test.page'));
const MediaPage = page(() => import('./features/media/media.page'));
const CalendarPage = page(() => import('./features/bookings/calendar.page'));
const RequestsPage = page(() => import('./features/bookings/requests.page'));
const AvailabilityPage = page(() => import('./features/bookings/availability.page'));
const WizardPage = page(() => import('./features/onboarding/wizard.page'));
const ApprovalsPage = page(() => import('./features/simulation/approvals.page'));
const SettingsLayout = page(() => import('./features/settings/settings.layout'));
const IntegrationsPage = page(() => import('./features/settings/integrations.page'));
const ClinicsPage = page(() => import('./features/settings/clinics.page'));
const PlatformOverviewPage = page(() => import('./features/platform/overview.page'));
const ClinicDirectoryPage = page(() => import('./features/platform/directory.page'));
const ClinicDetailPage = page(() => import('./features/platform/clinic-detail.page'));
const StaffPage = page(() => import('./features/platform/staff.page'));
const PlansPage = page(() => import('./features/platform/plans.page'));
const CouponsPage = page(() => import('./features/platform/coupons.page'));
const TreatmentLibraryPage = page(() => import('./features/platform/treatment-library.page'));
const MediaLibraryPage = page(() => import('./features/platform/media-library.page'));
const EmailsPage = page(() => import('./features/platform/emails.page'));
const ProfilePage = page(() => import('./features/settings/profile.page'));

export function AppRoutes() {
  const status = useAuth((s) => s.status);
  const hasClinic = useAuth((s) => Boolean(s.user?.tenantId));

  // Only once signed in: the login page should not pay for the dashboard.
  useEffect(() => {
    if (status === 'authenticated') preloadRoutes();
  }, [status]);

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* Where they were going is remembered, so signing in lands them there
            rather than dumping them on the dashboard. */}
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    );
  }

  // Two trees, chosen by who is signed in. Platform staff belong to no clinic,
  // so every clinic page could only fail for them; a separate tree means a typed
  // /scans lands on the console instead of a page full of 403s.
  return <AppShell>{hasClinic ? <TrialGate /> : <PlatformRoutes />}</AppShell>;
}

/**
 * Sign-up ends with a plan and a card (which start the free trial). Until then
 * a clinic sees only that step: the owner chooses, everyone else waits.
 */
function TrialGate() {
  const role = useAuth((s) => s.user?.role);
  const { data, isLoading } = useQuery({
    queryKey: keys.onboarding,
    queryFn: () => http.get<{ needsPlan?: boolean }>('/tenants/me/onboarding'),
  });
  if (isLoading) return null;
  if (data?.needsPlan) return can(role, 'billing.manage') ? <StartTrialPage /> : <WaitingForOwner />;
  return <ClinicRoutes />;
}

function ClinicRoutes() {
  return (
    <Routes>
      <Route path="/" element={<DashboardPage />} />
      <Route path="/onboarding" element={<Guarded capability="clinic.manage"><WizardPage /></Guarded>} />

      <Route path="/scans" element={<ScansPage />} />
      <Route path="/scans/:id" element={<ScanDetailPage />} />

      <Route path="/patients" element={<PatientsPage />} />
      <Route path="/patients/:id" element={<PatientDetailPage />} />

      <Route path="/treatments" element={<Guarded capability="catalogue.write"><TreatmentsPage /></Guarded>} />
      <Route path="/products" element={<Guarded capability="catalogue.write"><ProductsPage /></Guarded>} />

      <Route path="/bookings" element={<CalendarPage />} />
      <Route path="/bookings/requests" element={<RequestsPage />} />
      <Route path="/bookings/availability" element={<AvailabilityPage />} />

      <Route path="/leads" element={<LeadsPage />} />
      <Route path="/orders" element={<OrdersPage />} />
      <Route path="/analytics" element={<Guarded capability="analytics.read"><AnalyticsPage /></Guarded>} />

      <Route path="/widget" element={<Guarded capability="widget.configure"><WidgetConfigPage /></Guarded>} />
      <Route path="/widget/install" element={<Guarded capability="widget.configure"><WidgetInstallPage /></Guarded>} />
      <Route path="/widget/test" element={<Guarded capability="widget.configure"><WidgetTestPage /></Guarded>} />
      <Route path="/media" element={<Guarded capability="widget.configure"><MediaPage /></Guarded>} />
      <Route path="/domains" element={<Navigate to="/settings/domains" replace />} />

      <Route path="/simulations" element={<Guarded capability="simulations.approve"><ApprovalsPage /></Guarded>} />

      <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
      <Route path="/team" element={<Navigate to="/settings/team" replace />} />

      {/* Everything configurable lives under one section with its own nav.
          Each child keeps the capability guard it had as a top-level route. */}
      <Route path="/settings" element={<SettingsLayout />}>
        <Route index element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="team" element={<Guarded capability="team.manage"><TeamPage /></Guarded>} />
        <Route path="domains" element={<Guarded capability="domains.manage"><DomainsPage /></Guarded>} />
        <Route path="billing" element={<Guarded capability="billing.manage"><BillingPage /></Guarded>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Platform staff. The same guard mechanism as every other route — the
 * capability is simply one no clinic role holds.
 */
function PlatformRoutes() {
  return (
    <Routes>
      <Route path="/console" element={<Guarded capability="platform.manage"><PlatformOverviewPage /></Guarded>} />
      <Route path="/console/clinics" element={<Guarded capability="platform.manage"><ClinicDirectoryPage /></Guarded>} />
      <Route path="/console/clinics/:id" element={<Guarded capability="platform.manage"><ClinicDetailPage /></Guarded>} />
      <Route path="/console/plans" element={<Guarded capability="platform.manage"><PlansPage /></Guarded>} />
      <Route path="/console/coupons" element={<Guarded capability="platform.manage"><CouponsPage /></Guarded>} />
      <Route path="/console/treatment-library" element={<Guarded capability="platform.manage"><TreatmentLibraryPage /></Guarded>} />
      <Route path="/console/media" element={<Guarded capability="platform.manage"><MediaLibraryPage /></Guarded>} />
      <Route path="/console/emails" element={<Guarded capability="platform.manage"><EmailsPage /></Guarded>} />
      {/* The console used to live under /platform — keep old links and bookmarks working. */}
      <Route path="/platform/*" element={<ConsoleRedirect />} />

      <Route path="/settings" element={<SettingsLayout />}>
        {/* General settings are a clinic's own; an operator's first page is their profile. */}
        <Route index element={<Navigate to="/settings/profile" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="integrations" element={<Guarded capability="platform.manage"><IntegrationsPage /></Guarded>} />
        <Route path="clinics" element={<Guarded capability="platform.verify"><ClinicsPage /></Guarded>} />
        <Route path="staff" element={<Guarded capability="platform.manage"><StaffPage /></Guarded>} />
      </Route>

      <Route path="*" element={<Navigate to="/console" replace />} />
    </Routes>
  );
}

/** `/platform/…` → `/console/…`, keeping the rest of the path, the query and the hash. */
function ConsoleRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/platform(?=\/|$)/, '/console') + search + hash} replace />;
}

/**
 * Hides a route from a role that cannot use it. The API's RolesGuard is what
 * actually enforces this — a guard here only saves someone a pointless 403.
 */
function Guarded({ capability, children }: { capability: Capability; children: React.ReactNode }) {
  const role = useAuth((s) => s.user?.role);
  return can(role, capability) ? <>{children}</> : <Navigate to="/" replace />;
}

function RedirectToLogin() {
  const location = useLocation();
  return <Navigate to="/login" state={{ from: location.pathname }} replace />;
}
